import React, { useState, FC, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';

import { Button, Tooltip } from '@coinflare/ui/core';

import { Checkbox } from 'src/components/core';
import { Flash } from 'src/components/core/icons';
import { FlexContainer } from 'src/components/core/layout/FlexContainer';
import { BASE_URL } from 'src/config';
import { useAppSelector, useLocalStorage } from 'src/hooks';

interface SwitchButtonProps {
    active: boolean;
}

interface SimplifiedOrProTradeSwitcherProps {
    isDerivative: boolean;
}

const SwitchesWrapper = styled.div`
    display: flex;
    position: relative;

    > div:nth-child(1) {
        border-top-left-radius: 4px;
        border-bottom-left-radius: 4px;
        border-right: none;
    }

    > div:nth-child(2) {
        border-top-right-radius: 4px;
        border-bottom-right-radius: 4px;
        border-left: none;
    }

    span {
        min-width: unset !important;
    }
`;

const SwitchButton = styled.div<SwitchButtonProps>`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 4px 10px;
    cursor: pointer;
    ${({ active, theme: { colors, typography, breakpoints } }) => `
        font-size: ${typography.size.desktop.paragraph.p2};
        font-weight: ${typography.weight.medium};
        background-color: ${active ? colors.text.primary : 'transparent'};
        color: ${active ? colors.background.primary : colors.text.tertiary};
        border: 1px solid ${active ? colors.text.primary : colors.border.dark};


        @media (max-width: ${breakpoints.md}) {
            font-size: ${typography.size.desktop.paragraph.p3};
        }
    `}
`;

const InfoPopupContainer = styled.div`
    position: absolute;
    left: 100%;
    transform: translateX(-100%);
    color: #fff;
    padding: 16px;
    border-radius: 6px;
    z-index: 1;
    top: 180%;
    white-space: pre;
    box-shadow: 0 0 12px rgba(0, 0, 0, 0.2);
    white-space: normal;
    width: 250px;

    ${({ theme: { colors, typography, isDark } }) => `
        background-color: ${isDark ? colors.text.primary : colors.background.primary};
        color: ${colors.text.accent};
        font-size: ${typography.size.desktop.paragraph.p2};
        font-weight: ${typography.weight.normal};
    `}
`;

const PopupArrow = styled.div`
    position: absolute;
    bottom: 100%;
    left: 80%;
    transform: translateX(-80%);
    width: 0;
    height: 0;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;

    ${({ theme: { colors, isDark } }) => `
        border-bottom: 6px solid ${isDark ? colors.text.primary : colors.background.primary};
    `}
`;

const InfoPopupComponent = () => {
    const { t } = useTranslation();
    const [checked, setChecked] = useState(false);
    const [show, setShow] = useState(true);

    const [showSimplifiedOrProTradeInfoPopup, setShowSimplifiedOrProTradeInfoPopup] =
        useLocalStorage('showSimplifiedOrProTradeInfoPopup', true);

    if (!show || !showSimplifiedOrProTradeInfoPopup) {
        return null;
    }

    return (
        <InfoPopupContainer>
            <PopupArrow />
            {t('trade_info_popup_text')}
            <FlexContainer
                styles={{
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: 8,
                    gap: 16,
                }}
            >
                <Checkbox
                    label={t('not_show_again')}
                    value={checked}
                    onChange={(val) => {
                        setChecked(val);
                    }}
                />
                <Button
                    onClick={() => {
                        setShow(false);
                        checked && setShowSimplifiedOrProTradeInfoPopup(false);
                    }}
                    text={t('close')}
                    variant={'brandSmall'}
                    style={{ height: 30 }}
                />
            </FlexContainer>
        </InfoPopupContainer>
    );
};

const SimplifiedOrProTradeSwitcher: FC<SimplifiedOrProTradeSwitcherProps> = ({ isDerivative }) => {
    const { t } = useTranslation();
    const { isDark } = useTheme();

    const [isSimplifiedTradeActive, setIsSimplifiedTradeActive] = useLocalStorage(
        'isSimplifiedTradeActive',
        false,
        true,
    );

    const { currentSymbol: currentSymbolDerivative } = useAppSelector((state) => state.derivatives);
    const { currentSymbol: currentSymbolSpot } = useAppSelector((state) => state.spot);

    const simplifiedTradePath = isDerivative ? 'trade-contract' : 'trade-spot';
    const proTradePath = isDerivative ? 'trade/contract' : 'trade/spot';

    const currentSymbol = useMemo(() => {
        return isDerivative ? currentSymbolDerivative : currentSymbolSpot;
    }, [isDerivative, currentSymbolDerivative, currentSymbolSpot]);

    const clickHandler = useCallback(
        (value: boolean) => {
            setIsSimplifiedTradeActive(value);

            if (
                (value && window.location.pathname.includes(simplifiedTradePath)) ||
                (!value && window.location.pathname.includes(proTradePath))
            ) {
                return;
            }

            window.open(
                `${BASE_URL}/${value ? simplifiedTradePath : proTradePath}/${
                    currentSymbol.symbolId
                }`,
                '_self',
            );
        },
        [currentSymbol, window.location.pathname],
    );

    return (
        <SwitchesWrapper>
            <Tooltip
                title={t('simplified_trade_tooltip_text')}
                placement={'bottom'}
                theme={isDark ? 'dark' : 'light'}
            >
                <SwitchButton
                    active={isSimplifiedTradeActive}
                    onClick={() => {
                        clickHandler(true);
                    }}
                >
                    <Flash />
                    Flash
                </SwitchButton>
            </Tooltip>

            <Tooltip
                title={t('pro_trade_tooltip_text')}
                placement={'bottom'}
                theme={isDark ? 'dark' : 'light'}
            >
                <SwitchButton
                    active={!isSimplifiedTradeActive}
                    onClick={() => {
                        clickHandler(false);
                    }}
                >
                    Pro
                </SwitchButton>
            </Tooltip>

            <InfoPopupComponent />
        </SwitchesWrapper>
    );
};

export { SimplifiedOrProTradeSwitcher };
